import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  FileText,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Menu,
  Send,
  Smartphone,
  UserCog,
  Users,
  Webhook,
} from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { cn } from "../lib/cn";
import { CommandPalette } from "../components/ui/CommandPalette";
import { OfflineBanner } from "../components/ui/OfflineBanner";
import { useAuthStore } from "../stores/auth-store";

/** Exported so the command palette navigates to exactly the same set the sidebar shows —
 * a second copy of this list would silently drift the moment a page is added. */
export const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/messaging", label: "Messaging", icon: Send },
  { to: "/templates", label: "Templates", icon: FileText },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/devices", label: "Devices", icon: Smartphone },
  { to: "/webhooks", label: "Webhooks", icon: Webhook },
  { to: "/api-keys", label: "API Keys", icon: KeyRound },
  { to: "/account", label: "Account", icon: UserCog },
] as const;

function Brand() {
  return (
    <div className="flex h-16 items-center gap-2.5 border-b border-border px-5">
      <span className="flex size-8 items-center justify-center rounded-control bg-accent shadow-sm">
        <MessageSquare className="size-4 text-white" aria-hidden />
      </span>
      <span className="font-semibold tracking-tight text-text">SMS Fort</span>
    </div>
  );
}

/**
 * One nav list, rendered by both the desktop sidebar and the mobile drawer.
 *
 * The sidebar was `hidden md:flex` with no drawer and no menu button, so below 768px the app
 * had no navigation at all — every route was reachable only by typing its URL.
 */
function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="flex flex-1 flex-col gap-0.5 p-3">
      {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "relative flex items-center gap-3 rounded-control px-3 py-2.5 text-sm font-medium",
              "transition-colors duration-fast ease-standard",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
              isActive
                ? "bg-accent-soft text-accent"
                : "text-text-muted hover:bg-surface-hover hover:text-text",
            )
          }
        >
          {({ isActive }) => (
            <>
              {/* A 2px accent rail on the active item. The soft fill alone reads as a hover
                  state; the rail is what makes the current page unambiguous at a glance. */}
              <span
                aria-hidden
                className={cn(
                  "absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-pill bg-accent",
                  "transition-opacity duration-fast ease-standard",
                  isActive ? "opacity-100" : "opacity-0",
                )}
              />
              <Icon className="size-4 shrink-0" aria-hidden />
              {label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function AppShell() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // Resizing past the md breakpoint reveals the permanent sidebar; leaving the drawer mounted
  // would keep the page scroll-locked behind an overlay the user can no longer see.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const close = () => setNavOpen(false);
    mq.addEventListener("change", close);
    return () => mq.removeEventListener("change", close);
  }, []);

  async function handleLogout() {
    setLoggingOut(true);
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <>
      <CommandPalette />
      <div className="flex min-h-screen flex-col bg-bg">
        <OfflineBanner />
        <div className="flex min-h-0 flex-1">
      <aside className="fixed inset-y-0 left-0 hidden w-60 flex-col border-r border-border bg-surface md:flex">
        <Brand />
        <SidebarNav />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col md:pl-60">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-bg px-4 sm:px-6">
          <Dialog.Root open={navOpen} onOpenChange={setNavOpen}>
            <Dialog.Trigger
              aria-label="Open navigation"
              className={cn(
                "-ml-1 inline-flex size-9 items-center justify-center rounded-control text-text-muted md:hidden",
                "transition-colors duration-fast ease-standard hover:bg-surface-hover hover:text-text",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
              )}
            >
              <Menu className="size-5" aria-hidden />
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px] data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out md:hidden" />
              <Dialog.Content
                className={cn(
                  "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border bg-surface shadow-lg md:hidden",
                  "focus:outline-none",
                  "data-[state=open]:animate-drawer-in data-[state=closed]:animate-drawer-out",
                )}
              >
                <Dialog.Title className="sr-only">Navigation</Dialog.Title>
                <Brand />
                {/* Closing on navigate: otherwise the drawer stays open over the page it just
                    moved to, which reads as the tap having failed. */}
                <SidebarNav onNavigate={() => setNavOpen(false)} />
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          <span className="font-semibold tracking-tight text-text md:hidden">SMS Fort</span>

          <div className="ml-auto flex items-center gap-4">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              className={cn(
                "flex items-center gap-2 rounded-control px-2 py-1.5",
                "transition-colors duration-fast hover:bg-surface-hover"
              )}
            >
              <span className="flex size-8 items-center justify-center rounded-pill bg-accent text-xs font-semibold text-white">
                {user ? initialsOf(user.name) : "?"}
              </span>
              <span className="hidden text-sm font-medium text-text sm:inline">
                {user?.name}
              </span>
              <ChevronDown className="size-4 text-text-muted" aria-hidden />
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={8}
                className="z-50 w-56 rounded-card border border-border bg-surface p-1.5 shadow-lg"
              >
                <div className="px-2.5 py-2">
                  <p className="truncate text-sm font-medium text-text">
                    {user?.name}
                  </p>
                  <p className="truncate text-xs text-text-muted">
                    {user?.email}
                  </p>
                </div>
                <DropdownMenu.Separator className="my-1 h-px bg-border" />
                <DropdownMenu.Item
                  onSelect={() => navigate("/account")}
                  className="flex cursor-pointer items-center gap-2 rounded-control px-2.5 py-2 text-sm text-text outline-none data-[highlighted]:bg-surface-hover"
                >
                  <UserCog className="size-4" aria-hidden />
                  Account settings
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={(e) => {
                    e.preventDefault();
                    void handleLogout();
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-control px-2.5 py-2 text-sm text-danger outline-none data-[highlighted]:bg-danger-soft"
                >
                  <LogOut className="size-4" aria-hidden />
                  {loggingOut ? "Signing out…" : "Log out"}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          </div>
        </header>

        {/*
          Content width and page padding live here, not on each page. Pages had been setting
          their own `mx-auto max-w-*`, which is why Dashboard, Messaging and API Keys each sat
          at a different width. 1200px keeps table rows scannable on a 1920px display rather
          than stretching a six-column table across the full viewport.
        */}
        <main className="flex-1 px-4 py-6 sm:px-6 sm:py-8">
          <div className="mx-auto w-full max-w-[1200px]">
            <Outlet />
          </div>
        </main>
        </div>
        </div>
      </div>
    </>
  );
}
